import { useEffect, useState } from "react";
import { streamSimulation, fetchMatch } from "../lib/api";

/** Hook that builds a Match state shape, either by streaming a fresh sim or
 *  loading an existing match by id. Returns
 *  { meta, events, currentMinute, final, status, error, matchId }.
 */
export function useMatchSource({ routeId, teamA, teamB, knockout, version }) {
  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState([]);
  const [currentMinute, setCurrentMinute] = useState(0);
  const [final, setFinal] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [matchId, setMatchId] = useState(null);

  // Replay (load existing match by id)
  useEffect(() => {
    if (!routeId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchMatch(routeId);
        if (cancelled) return;
        setMeta({
          type: "meta",
          id: m.id,
          team_a: m.team_a,
          team_b: m.team_b,
          stadium: m.stadium,
          breakdown_a: m.breakdown_a,
          breakdown_b: m.breakdown_b,
          knockout: m.knockout,
        });
        setEvents(m.events || []);
        setFinal({
          type: "fulltime",
          score: m.score,
          extra_time: m.extra_time,
          penalties: m.penalties,
          pens: m.pens,
          pen_sequence: m.pen_sequence,
          xg: m.xg,
          possession: m.possession,
          shots: m.shots,
          shots_on_target: m.shots_on_target,
          man_of_the_match: m.man_of_the_match,
        });
        setStatus("done");
        setMatchId(m.id);
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to load match by id:", e);
        setStatus("error");
        setError("Match not found");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  // Live stream (fresh simulation)
  useEffect(() => {
    if (routeId) return undefined;
    if (!teamA || !teamB) return undefined;
    setStatus("loading");
    setMeta(null);
    setEvents([]);
    setCurrentMinute(0);
    setFinal(null);
    setError(null);
    setMatchId(null);

    const ctrl = streamSimulation({ teamA, teamB, knockout }, (msg) => {
      switch (msg.type) {
        case "meta":
          setMeta(msg);
          setMatchId(msg.id);
          setStatus("live");
          break;
        case "kickoff":
          setCurrentMinute(1);
          break;
        case "event":
          setCurrentMinute(msg.event.minute);
          setEvents((prev) => [...prev, msg.event]);
          break;
        case "fulltime":
          setFinal(msg);
          break;
        case "done":
          setStatus("done");
          break;
        case "error":
          console.error("Stream error:", msg.message);
          setStatus("error");
          setError(msg.message);
          break;
        default:
          break;
      }
    });
    return () => ctrl.abort();
  }, [teamA, teamB, knockout, version, routeId]);

  return { meta, events, currentMinute, final, status, error, matchId };
}
